import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"

const completeFormSchema = z.object({
  addition_notes: z.string().optional(),
  media_file: z.any().optional(),
})

export function CompleteAppointmentDialog({
  open,
  onOpenChange,
  onComplete,
  appointment,
}) {
  const [file, setFile] = useState(null)
  const form = useForm({
    resolver: zodResolver(completeFormSchema),
    defaultValues: {
      addition_notes: appointment?.addition_notes || "",
      media_file: undefined,
    },
  })

  useEffect(() => {
    if (open && appointment) {
      form.reset({
        addition_notes: appointment.addition_notes || "",
        media_file: undefined,
      })
    }
  }, [open, appointment, form])

  const onSubmit = async (values) => {
    const formData = new FormData()

    // Handle file upload more explicitly
    if (file) {
      // Make sure to append with the correct file name
      formData.append("media_file", file, file.name)
    }

    // Append notes if they exist
    if (values.addition_notes?.trim()) {
      formData.append("addition_notes", values.addition_notes)
    }

    try {
      await onComplete(formData)
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Complete Appointment</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="media_file"
              render={() => (
                <FormItem>
                  <FormLabel>Upload Prescription (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0]
                        if (
                          selectedFile &&
                          selectedFile.size > 5 * 1024 * 1024
                        ) {
                          alert("File size should not exceed 5MB")
                          e.target.value = null
                          return
                        }
                        setFile(selectedFile)
                      }}
                    />
                  </FormControl>
                  <p className="text-sm text-gray-500">
                    Maximum file size: 5MB. Accepted formats: PDF, JPG, PNG
                  </p>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="addition_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter detailed notes about the appointment..."
                      className="min-h-[120px] resize-vertical"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Complete Appointment</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
